import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FiArrowRight, FiCopy, FiDownload, FiEdit2, FiSend } from 'react-icons/fi';
import api from '../../utils/api';
import { useSession } from '../../contexts/SessionContext';
import { useAuth } from '../../contexts/AuthContext';
import { puede } from '../../utils/permisos';
import ModalEnviarEmailCliente from '../ui/ModalEnviarEmailCliente';
import Presupuestos from '../../pages/Presupuestos';
import DocumentoEliminar from '../ui/DocumentoEliminar';
import { accionesDocumento } from '../../utils/accionesDocumento';

function fmtFecha(f) {
  if (!f) return '—';
  return new Date(f).toLocaleDateString('es-ES');
}

function fmtEuro(v) {
  return Number(v || 0).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' });
}

export default function PresupuestoSession({ sesion }) {
  const { abrirSesion, cerrarSesion } = useSession();
  const { usuario, empresa } = useAuth();
  const puedeClonar = puede(usuario, 'presupuestos', 'ver') && puede(usuario, 'presupuestos', 'crear');
  const [clonando, setClonando] = useState(false);
  const [errorClonacion, setErrorClonacion] = useState(null);
  const clonandoRef = useRef(false);
  const [presupuesto, setPresupuesto] = useState(null);
  const [editando, setEditando] = useState(false);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [modalEmailDoc, setModalEmailDoc] = useState(null);
  const puedeEnviar = puede(usuario, 'presupuestos', 'editar') || puede(usuario, 'presupuestos', 'crear') || puede(usuario, 'clientes', 'enviar_email');

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const { data } = await api.get(`/presupuestos/${sesion.entityId}`);
      setPresupuesto(data);
    } catch {
      setError('No se pudo cargar el presupuesto.');
    } finally {
      setCargando(false);
    }
  }, [sesion.entityId]);

  const abrirPDF = () => {
    const token = localStorage.getItem('trix_token');
    fetch(`/api/presupuestos/${presupuesto._id}/pdf`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        if (!r.ok) throw new Error();
        return r.blob();
      })
      .then((b) => window.open(URL.createObjectURL(b), '_blank'))
      .catch(() => setError('No se pudo generar el PDF'));
  };

  const convertirPedido = async () => {
    if (!accionesDocumento(usuario, empresa, 'presupuestos', presupuesto).convertir || clonandoRef.current) return;
    if (!window.confirm('¿Quieres convertir este presupuesto en pedido?')) return;
    if (!accionesDocumento(usuario, empresa, 'presupuestos', presupuesto).convertir) return;
    clonandoRef.current = true;
    try {
      const { data } = await api.post(`/presupuestos/${presupuesto._id}/convertir`);
      await cargar();
      abrirSesion('pedido', data._id, `Pedido #${data.numero}`);
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo convertir el presupuesto en pedido.');
    } finally {
      clonandoRef.current = false;
    }
  };

  const clonarPresupuesto = async () => {
    if (!puedeClonar || clonandoRef.current) return;
    if (!window.confirm('Se creará un nuevo presupuesto en borrador. El presupuesto original quedará sin cambios. ¿Deseas continuar?')) return;
    clonandoRef.current = true;
    setClonando(true);
    setErrorClonacion(null);
    try {
      const { data } = await api.post(`/presupuestos/${presupuesto._id}/clonar`);
      abrirSesion('presupuesto', data._id, `Presupuesto #${data.numeroCustom || data.numero}`);
    } catch (err) {
      setErrorClonacion(err.response?.data?.error || 'No se pudo clonar el presupuesto');
    } finally {
      clonandoRef.current = false;
      setClonando(false);
    }
  };

  const abrirModalEmail = async () => {
    if (!puedeEnviar) return;
    let clienteObj = typeof presupuesto.cliente === 'object' && presupuesto.cliente ? presupuesto.cliente : { nombre: 'Cliente', email: '', telefono: '' };
    const clienteId = presupuesto.clienteId?._id || presupuesto.clienteId || presupuesto.cliente;

    if (clienteId && (!clienteObj.email || !clienteObj.nombre)) {
      try {
        const { data } = await api.get(`/clientes/${clienteId}`);
        clienteObj = data || clienteObj;
      } catch {
        // Si no se encuentra o no está poblado, se mantiene el cliente mínimo y el modal mostrará vacío si no hay email.
      }
    }

    const numeroVisible = String(presupuesto.numeroCustom || presupuesto.numero || '').trim();
    const cuerpoTexto =
      `Estimado/a ${clienteObj.nombre || 'cliente'},\n\n` +
      `Adjunto el presupuesto con referencia ${numeroVisible || 'sin número'} para su revisión.\n\n` +
      `Puedes revisarlo y responder cuando quieras.\n\n` +
      `Un saludo.`;

    setModalEmailDoc({
      cliente: clienteObj,
      asunto: `Presupuesto ${numeroVisible || ''}`.trim(),
      cuerpoTexto,
      tipo: 'presupuesto',
      documentoId: presupuesto._id,
      documentoTipo: 'presupuesto',
      numeroDocumento: numeroVisible,
      numeroCustom: presupuesto.numeroCustom || '',
    });
  };

  useEffect(() => { setEditando(false); cargar(); }, [cargar]);

  if (cargando) return <div className="session-content"><p className="empty">Cargando…</p></div>;
  if (error) return <div className="session-content"><div className="alert-error">{error}</div></div>;
  if (!presupuesto) return null;

  const lineas = presupuesto.lineas || [];
  const puedeEditar = puede(usuario, 'presupuestos', 'editar') && presupuesto.estado !== 'convertido' && !presupuesto.firmaInfo?.firmado;
  const disponibles = accionesDocumento(usuario, empresa, 'presupuestos', presupuesto);
  const clienteObj = typeof presupuesto.cliente === 'object' && presupuesto.cliente ? presupuesto.cliente : { nombre: 'Cliente', email: '', telefono: '' };

  return (
    <div className="session-content">
      <div className="session-panels" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', marginBottom: 24, padding: 0 }}>
        <div className="session-panel">
          <div className="session-panel-title">Número</div>
          <div className="session-kpi">#{presupuesto.numero || '—'}</div>
        </div>
        <div className="session-panel">
          <div className="session-panel-title">Fecha</div>
          <div className="session-kpi" style={{ fontSize: 18 }}>{fmtFecha(presupuesto.fecha || presupuesto.createdAt)}</div>
        </div>
        <div className="session-panel">
          <div className="session-panel-title">Estado</div>
          <div className="session-kpi" style={{ fontSize: 18 }}>{presupuesto.estado || '—'}</div>
        </div>
        <div className="session-panel">
          <div className="session-panel-title">Total</div>
          <div className="session-kpi">{fmtEuro(presupuesto.total)}</div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, marginBottom: 18 }}>
        {puedeEditar && (
          <button type="button" className="btn" aria-label="Editar presupuesto" onClick={() => {
            if (puede(usuario, 'presupuestos', 'editar') && presupuesto.estado !== 'convertido' && !presupuesto.firmaInfo?.firmado) setEditando(true);
          }} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            <FiEdit2 /> Editar presupuesto
          </button>
        )}
        {puedeClonar && (
          <button type="button" className="btn" aria-label="Clonar presupuesto" onClick={clonarPresupuesto} disabled={clonando} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            <FiCopy /> {clonando ? 'Clonando...' : 'Clonar presupuesto'}
          </button>
        )}
        {puedeEnviar && <button type="button" className="btn btn-primary" onClick={abrirModalEmail} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          <FiSend /> Enviar
        </button>}
        <button type="button" className="btn" onClick={abrirPDF} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          <FiDownload /> Descargar PDF
        </button>
        {puede(usuario, 'presupuestos', 'editar') && <button type="button" className="btn" onClick={convertirPedido} disabled={!disponibles.convertir} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          <FiArrowRight /> {presupuesto.estado === 'convertido' ? 'Pedido ya convertido' : 'Convertir en pedido'}
        </button>}
        <DocumentoEliminar tipo="presupuestos" documento={presupuesto} onEliminado={() => cerrarSesion(sesion.id)} />
      </div>

      {errorClonacion && <div className="alert-error" role="alert">{errorClonacion}</div>}

      {editando && puedeEditar && <Presupuestos presupuestoInicial={presupuesto} onCerrarPresupuesto={() => setEditando(false)} onPresupuestoGuardado={async () => { setEditando(false); await cargar(); }} onDocumentoEliminado={() => cerrarSesion(sesion.id)} />}

      {presupuesto.cliente && (
        <p style={{ marginBottom: 16, fontSize: 14, color: 'var(--text-soft)' }}>
          <strong>Cliente:</strong> {clienteObj?.nombre || presupuesto.cliente}
        </p>
      )}

      <div style={{ marginBottom: 16 }}>
        <strong>Observaciones</strong>
        <p style={{ whiteSpace: 'pre-wrap' }}>{(presupuesto.observaciones ?? presupuesto.notas ?? '') || 'Sin observaciones'}</p>
      </div>

      {lineas.length > 0 && (
        <div className="table-wrap" style={{ marginTop: 8 }}>
          <table className="data-table">
            <thead>
              <tr><th>Descripción</th><th>Cant.</th><th>Precio unit.</th><th>Total</th></tr>
            </thead>
            <tbody>
              {lineas.map((l, i) => (
                <tr key={i}>
                  <td>{l.descripcion || '—'}</td>
                  <td>{l.cantidad ?? '—'}</td>
                  <td>{fmtEuro(l.precioUnitario)}</td>
                  <td>{fmtEuro(l.total ?? (l.cantidad * l.precioUnitario))}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '12px 16px', fontSize: 15, gap: 24 }}>
            {presupuesto.subtotal != null && <span>Subtotal: <strong>{fmtEuro(presupuesto.subtotal)}</strong></span>}
            {presupuesto.iva != null && <span>IVA: <strong>{fmtEuro(presupuesto.iva)}</strong></span>}
            <span>Total: <strong>{fmtEuro(presupuesto.total)}</strong></span>
          </div>
        </div>
      )}

      <ModalEnviarEmailCliente
        abierto={!!modalEmailDoc}
        onCerrar={() => setModalEmailDoc(null)}
        cliente={modalEmailDoc?.cliente}
        asuntoInicial={modalEmailDoc?.asunto}
        cuerpoTextoInicial={modalEmailDoc?.cuerpoTexto}
        tipoInicial={modalEmailDoc?.tipo}
        documentoId={modalEmailDoc?.documentoId}
        documentoTipo={modalEmailDoc?.documentoTipo}
        numeroDocumento={modalEmailDoc?.numeroDocumento}
        numeroCustom={modalEmailDoc?.numeroCustom}
        onEnviado={() => setModalEmailDoc(null)}
      />
    </div>
  );
}
